import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Project } from '../../types/work';
import { WorkStatusBadge } from './WorkStatusBadge';
import { PriorityChip } from './PriorityChip';
import { DueDateLabel } from './DueDateLabel';
import UserBadge from '../common/UserBadge';

interface Props {
  project: Project;
  onClick: () => void;
  goalTitle?: string;
}

export const ProjectCard: React.FC<Props> = ({ project, onClick, goalTitle }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // Date range track calculation
  const startMs = project.start_date ? new Date(project.start_date).getTime() : null;
  const dueMs = project.due_date ? new Date(project.due_date).getTime() : null;
  const nowMs = Date.now();
  const isOverdue = dueMs ? nowMs > dueMs && project.status !== 'completed' : false;

  let progressPct = 0;
  if (startMs && dueMs && dueMs > startMs) {
    progressPct = Math.min(100, Math.max(0, ((nowMs - startMs) / (dueMs - startMs)) * 100));
  }

  return (
    <div
      onClick={onClick}
      className={`rounded-xl border-l-4 border-l-teal-500 border overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.01] hover:-translate-y-px ${
        darkMode
          ? 'bg-gray-800 border-gray-700 hover:shadow-lg hover:shadow-teal-500/10'
          : 'bg-white border-gray-200 hover:shadow-lg'
      }`}
    >
      <div className="px-4 py-3 space-y-2.5">
        {/* Header: title + goal badge */}
        <div>
          <h3 className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{project.title}</h3>
          {goalTitle ? (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/goals/${project.goal_id}`); }}
              className="text-xs text-emerald-400 hover:underline flex items-center gap-1 mt-0.5"
            >
              🎯 {goalTitle}
            </button>
          ) : (
            <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Standalone</span>
          )}
        </div>

        {/* Status + Priority + Due */}
        <div className="flex items-center gap-2 flex-wrap">
          <WorkStatusBadge status={project.status} />
          <PriorityChip priority={project.priority} />
          <DueDateLabel date={project.due_date} />
        </div>

        {/* Date range track */}
        {startMs && dueMs && (
          <div className="relative">
            <div className={`h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className={`h-full rounded-full transition-all duration-500 ${isOverdue ? 'bg-red-500' : 'bg-teal-500'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {/* Today marker */}
            {progressPct > 0 && progressPct < 100 && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white ring-2 ring-teal-500"
                style={{ left: `${progressPct}%`, marginLeft: '-5px' }}
              />
            )}
          </div>
        )}

        {/* Task count + Owner */}
        <div className="flex items-center justify-between">
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {project.task_count ?? 0} task{(project.task_count ?? 0) !== 1 ? 's' : ''}
          </span>
          <UserBadge owner={project.owner} size="sm" />
        </div>
      </div>
    </div>
  );
};
